// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, CircularProgress } from "@mui/material";
import { useState, useMemo } from "react";
import { Button } from "react-admin";
import { TargetSchema, type DocumentTransformation, type TransformationTemplate } from "@/types";
import { MappingTable } from "./MappingTable";
import type { CrossSchemaMappingInfo } from "./MappingRow";
import { SchemaSelector } from "./SchemaSelector";
import { MappingSummary } from "./MappingSummary";
import { HorizontalMappingGrid } from "./HorizontalMappingGrid";
import { PdfMappingSurface } from "./PdfMappingSurface";
import { FieldMappingEmptyState } from "./FieldMappingEmptyState";
import { featureFlags } from "@/utils/featureFlags";
import { useFieldMappingSave } from "../../hooks/useFieldMappingSave";
import { useFieldMappingState } from "../../hooks/useFieldMappingState";
import { getSaveButtonText, isSaveDisabled } from "../../utils/fieldMappingUtils";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";
import { CRITICAL_FIELDS, getCriticalCoverage } from "../document-review/criticalFields";
import { CriticalFieldGapList } from "./CriticalFieldGapList";
import { CriticalFieldAdvisoryContext, getUnmappedCriticalSlots } from "../../hooks/useCriticalFieldAdvisory";
import { useTransformationRecords } from "../../hooks/useTransformationRecords";

interface FieldMappingDialogProps {
  open: boolean;
  onClose: () => void;
  transformation: DocumentTransformation;
  transformationTemplates: TransformationTemplate[];
  mappingValidationIssues?: Array<{ schema: TargetSchema; message: string }>;
  clientId: string;
  /** Explicit document ID — used when transformation.document_id_ref is empty */
  documentId?: string;
  /**
   * Document-level context for critical-field advisory entries — drives
   * conditional filtering (denial_reason fires only when records have a
   * denial_amount, family-unit subscriber id only on family-deductible
   * policies, etc.). Optional: when omitted, conditional entries default to
   * surfacing.
   */
  advisoryContext?: CriticalFieldAdvisoryContext;
  /** True when the source file is a spreadsheet (xlsx/xls/csv). Gates the
   *  horizontal source-mirrored remap. Defaults false so callers must confirm
   *  a spreadsheet to opt in. */
  isSpreadsheet?: boolean;
  /** True when the source file is a PDF. Gates the map-on-document remap
   *  (#6119). Defaults false. */
  isPdf?: boolean;
}

export const FieldMappingDialog = ({
  open,
  onClose,
  transformation,
  transformationTemplates,
  mappingValidationIssues,
  clientId,
  documentId,
  advisoryContext,
  isSpreadsheet = false,
  isPdf = false,
}: FieldMappingDialogProps) => {
  const [selectedSchemaIndex, setSelectedSchemaIndex] = useState(0);
  const [showUnmapped, setShowUnmapped] = useState(true);

  // Records are fetched here rather than taken as a prop (#6755), so all three
  // call sites stay unchanged. The transformation row no longer carries them:
  // `?expand=extracted_records` was removed because serialising a
  // 497,711-record set OOM-kills mgmt-api (#6826).
  //
  // The mapping surfaces only ever needed a SAMPLE — the horizontal grid slices
  // the first PREVIEW_ROW_COUNT rows, and header inference reads a handful — so
  // one page is sufficient and the dialog does not page further.
  const { records: sampleRecords } = useTransformationRecords({
    documentId: documentId || transformation.document_id_ref,
    transformationId: transformation.id,
    enabled: open && !!transformation.id,
    autoLoadUpTo: 0,
  });

  const {
    template,
    schemaAnalyses,
    headerMappings,
    availableFields,
    currentEditedMappings,
    editedMappingsBySchema,
    schemasWithChanges,
    validationErrors,
    validSchemas,
    invalidSchemas,
    canSave,
    handleMappingChange,
    allSchemaKeys,
    fieldsBySchema,
    handleUnifiedMappingChange,
    isLoading,
  } = useFieldMappingState({
    transformation,
    records: sampleRecords,
    transformationTemplates,
    selectedSchemaIndex,
    showUnmapped,
  });

  // Source-mirrored remap router (behind the redesign flag): a spreadsheet gets
  // the horizontal grid (#6101), a PDF gets the map-on-document surface (#6119),
  // and anything else falls back to the classic schema-tabbed dialog.
  const useHorizontalRemap = featureFlags.documentReviewRedesignEnabled && isSpreadsheet;
  const usePdfRemap = featureFlags.documentReviewRedesignEnabled && isPdf && !isSpreadsheet;
  const useSourceMirror = useHorizontalRemap || usePdfRemap;
  const detectedHeaders = transformation.processing_metadata?.detected_headers ?? [];

  // Get current schema key
  const currentSchemaKey = schemaAnalyses[selectedSchemaIndex]?.target_schema || "";

  // Get current schema's validation errors
  const currentSchemaErrors = validationErrors[currentSchemaKey] || [];

  // Derive fields with inline errors (duplicates + mutually-exclusive conflicts) for the current schema
  const duplicateTargets = useMemo(() => {
    const targets = new Set<string>();
    currentSchemaErrors.forEach((error) => {
      if (error.type === "duplicate_target" || error.type === "mutually_exclusive") {
        targets.add(error.targetField);
      }
    });
    return targets;
  }, [currentSchemaErrors]);

  // Critical-field coverage for the current schema — drives the dialog header
  // banner. Includes both saved and in-flight edited mappings so the count
  // updates live as the reviewer changes mappings.
  const criticalCoverage = useMemo(() => {
    const mappedFields = Object.values(currentEditedMappings).flat();
    return getCriticalCoverage(currentSchemaKey as TargetSchema, mappedFields);
  }, [currentEditedMappings, currentSchemaKey]);

  // Unmapped critical slots for the current schema, with conditional entries
  // filtered against document context. Drives the in-dialog advisory list.
  // Re-derived from live edited mappings so the list shrinks as the reviewer
  // maps fields, without waiting for a save round-trip.
  const currentSchemaUnmapped = useMemo(() => {
    if (!currentSchemaKey) return [];
    const mappedFields = Object.values(currentEditedMappings).flat();
    return getUnmappedCriticalSlots(currentSchemaKey as TargetSchema, mappedFields, advisoryContext ?? {});
  }, [currentEditedMappings, currentSchemaKey, advisoryContext]);

  // Critical-field entries for the current schema — passed to MappingTable so
  // each dropdown option can render a severity chip + impact tooltip.
  const currentSchemaCritical = useMemo(
    () => CRITICAL_FIELDS[currentSchemaKey as TargetSchema] ?? [],
    [currentSchemaKey]
  );

  // Per-detected-header lookup of mappings the same header has in OTHER
  // schemas. Each MappingRow uses this to render an "also mapped in {Schema}"
  // chip so reviewers see at a glance that a header is doing work on other
  // tabs and don't need to wonder whether to remap it here.
  const crossSchemaMappingsByHeader = useMemo(() => {
    const result = new Map<string, CrossSchemaMappingInfo[]>();
    for (const [schemaKey, mappings] of Object.entries(editedMappingsBySchema)) {
      if (schemaKey === currentSchemaKey) continue;
      for (const [header, fields] of Object.entries(mappings)) {
        if (!fields || fields.length === 0) continue;
        const list = result.get(header) ?? [];
        list.push({ schema: schemaKey as TargetSchema, fields });
        result.set(header, list);
      }
    }
    return result;
  }, [editedMappingsBySchema, currentSchemaKey]);

  const { handleSave, isSaving } = useFieldMappingSave({
    transformation,
    template,
    schemasWithChanges,
    validSchemas,
    invalidSchemas,
    editedMappingsBySchema,
    onClose,
    clientId,
    documentId,
  });

  // Prevent showing incorrect/incomplete data while loading schema data from API
  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h3" component="div">
            Field Mapping Review
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle empty states
  if (!template) {
    return <FieldMappingEmptyState open={open} onClose={onClose} message="No transformation template available" />;
  }

  if (schemaAnalyses.length === 0) {
    return <FieldMappingEmptyState open={open} onClose={onClose} message="No schema analysis available" />;
  }

  // Get save button text and disabled state
  const saveButtonText = getSaveButtonText({
    isSaving,
    validSchemas,
    invalidSchemas,
    schemasWithChanges,
  });

  const saveButtonDisabled = isSaveDisabled(isSaving, canSave);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={useHorizontalRemap ? "xl" : "lg"} fullWidth>
      <DialogTitle>
        <Typography variant="h3" component="div">
          Field Mapping Review
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {useHorizontalRemap
            ? "Each column is from your source file, left to right. Map it to a field in any record type."
            : usePdfRemap
              ? "Each field is from your document. Map it to a field in any record type — select a row to find it in the PDF."
              : "Review and adjust how detected headers map to schema fields"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: useSourceMirror ? 2 : 0 }}>
        {useHorizontalRemap ? (
          <HorizontalMappingGrid
            transformation={transformation}
            records={sampleRecords}
            detectedHeaders={detectedHeaders}
            editedMappingsBySchema={editedMappingsBySchema}
            fieldsBySchema={fieldsBySchema}
            allSchemaKeys={allSchemaKeys}
            onChange={handleUnifiedMappingChange}
            advisoryContext={advisoryContext}
            validationErrors={validationErrors}
          />
        ) : usePdfRemap ? (
          <PdfMappingSurface
            transformation={transformation}
            records={sampleRecords}
            documentId={documentId}
            detectedHeaders={detectedHeaders}
            editedMappingsBySchema={editedMappingsBySchema}
            fieldsBySchema={fieldsBySchema}
            allSchemaKeys={allSchemaKeys}
            onChange={handleUnifiedMappingChange}
            advisoryContext={advisoryContext}
            validationErrors={validationErrors}
          />
        ) : (
          <>
            {/* Sticky tab strip — pins to the top of the scrolling DialogContent so
                the active-schema selection stays visible. DialogContent's pt is
                overridden to 0 above so top: 0 sits flush with the top edge —
                without that, top: 0 leaves a padding-sized gap where content
                peeks through. The schema name appears again on the table's
                "Maps to (Claimants)" column header, so a separate banner under
                the tabs is redundant. */}
            {schemaAnalyses.length > 1 && (
              <Box
                sx={(theme) => ({
                  position: "sticky",
                  top: 0,
                  mx: -3,
                  px: 3,
                  pt: 2,
                  pb: 1,
                  zIndex: 2,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: 1,
                  borderColor: "divider",
                  mb: 2,
                })}
              >
                <SchemaSelector
                  schemaAnalyses={schemaAnalyses}
                  selectedIndex={selectedSchemaIndex}
                  onChange={setSelectedSchemaIndex}
                  mappingValidationIssues={mappingValidationIssues}
                />
              </Box>
            )}

            {/* Summary info */}
            <MappingSummary
              showUnmapped={showUnmapped}
              onShowUnmappedChange={setShowUnmapped}
              criticalCoverage={criticalCoverage}
            />

            {/* Per-tab advisory: lists unmapped critical fields with severity +
                impact. Shrinks live as the reviewer maps fields and disappears
                when fully covered. Lives here (not the page sidebar) because the
                field-mapping dialog is where the advisory becomes actionable. */}
            <CriticalFieldGapList entries={currentSchemaUnmapped} />

            {/* Mapping table */}
            <MappingTable
              headerMappings={headerMappings}
              editedMappings={currentEditedMappings}
              availableFields={availableFields}
              criticalFields={currentSchemaCritical}
              crossSchemaMappingsByHeader={crossSchemaMappingsByHeader}
              onMappingChange={handleMappingChange}
              duplicateTargets={duplicateTargets}
              targetColumnLabel={`Maps to (${SCHEMA_LABELS[currentSchemaKey as TargetSchema] ?? "field"})`}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saveButtonDisabled}>
          {saveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
